import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Input,
    Heading,
    VStack,
    FormControl,
    FormLabel,
    Select,
    Textarea,
    Checkbox,
    Text,
    GridItem,
    Stepper,
    Step,
    StepIndicator,
    StepStatus,
    StepTitle,
    StepDescription,
    StepSeparator,
    StepIcon,
    StepNumber,
    Flex,
    useToast,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useColorMode,
    Card,
    CardBody,
    Skeleton,
    IconButton,
    Tooltip,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Badge,
    Progress,
    Divider,
    Code,
    HStack,
    MenuList,
    MenuItem,
    MenuButton,
    Menu
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit, MdRefresh, MdInfo, MdPlayArrow, MdWeb, MdApi } from 'react-icons/md';
import Navigation from '../components/Navigation';
import useCheckAuth from '../hooks/useCheckAuth';
import { Helmet } from "react-helmet";
import axios from '../utils/axiosConfig';

const MCPPage = () => {
    useCheckAuth();
    const { colorMode } = useColorMode();

    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingToolId, setEditingToolId] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        url: '',
        params: [],
        security: {
            headers: {},
        },
    });
    const [formErrors, setFormErrors] = useState({});
    const [selectedTool, setSelectedTool] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const toast = useToast();

    const steps = [
            { title: 'Basic Info', description: 'Enter the basic details' },
            { title: 'Security Settings', description: 'Configure security' },
            { title: 'Review', description: 'Review and save' }
        ];

    const maskUrlForTable = (url) => {
        if (!url) return '';
        if (url.length <= 18) return url;
        return `${url.slice(0, 18)}********`;
    };

    const fetchTools = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/protected/tools/mcp');
            setTools(response.data);
        } catch (error) {
            console.error('Error fetching mcps:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load mcps',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTools();
    }, []);

    const resetForm = () => {
        setFormData({
            name: '',
            url: '',
            security: {
                headers: {},
            }
        });
        setCurrentStep(0);
        setFormErrors({});
    };

    const isValidUrl = (url) => {
        const urlPattern = new RegExp(
            '^(https?:\\/\\/)?' + // Protocol
            '((([a-zA-Z0-9\\-\\.]+)\\.[a-zA-Z]{2,})|' + // Domain name
            'localhost|' + // Or localhost
            '\\d{1,3}(\\.\\d{1,3}){3})' + // Or IP (v4)
            '(\\:\\d+)?' + // Optional port
            '(\\/[-a-zA-Z0-9@:%_\\+.~#?&//=]*)?' + // Path
            '(\\?[;&a-zA-Z0-9%_\\+.~#?&=]*)?' + // Query string
            '(\\#[-a-zA-Z0-9_]*)?$' // Fragment locator
        );
        return urlPattern.test(url);
    };

    const isValidToolName = (name) => {
        if (!name) return false;
        const regex = /^[a-zA-Z0-9_-]{1,64}$/;
        return regex.test(name);
    };


    const handleToolNameChange = (e) => {
        const value = e.target.value;
        const regex = /^[a-zA-Z0-9_-]{1,64}$/;

        if (!regex.test(value) && value !== '') {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                name: 'Must be a-z, A-Z, 0-9, or contain underscores and dashes, with a maximum length of 64. No spacing allowed.',
            }));
        } else {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                name: '',
            }));
        }

        setFormData({
            ...formData,
            name: value,
        });
    };

    const handleUrlChange = (e) => {
        const value = e.target.value;
        const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

        if (!urlPattern.test(value) && value !== '') {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                url: 'Please enter a valid URL',
            }));
        } else {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                url: '',
            }));
        }

        setFormData({
            ...formData,
            url: value,
        });
    };

    const validateStep = (step) => {
        switch (step) {
            case 0:
                return formData.name.trim() !== '' &&
                    isValidToolName(formData.name) &&
                    isValidUrl(formData.url);
            case 1:
                return true;
            default:
                return true;
        }
    };


    const handleNext = () => {
        if (validateStep(currentStep)) {
                setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
        } else {
            toast({
                title: 'Error',
                description: 'Please fill in all required fields',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    }

    const handleAddTool = (type) => {
        resetForm();
        setEditingToolId(null);
        setShowForm(true);
        setFormData((prev) => ({
            ...prev
        }));
    };

    const handleCancel = () => {
        setShowForm(false);
        resetForm();
    };

    const handleEditTool = (tool) => {
        setFormData(tool);
        setEditingToolId(tool._id);

        // Set authType
        setAuthType(tool.security?.tokenType || 'none');

        console.log('Editing tool:', tool);
        setShowForm(true);
    };

    const handleDeleteTool = async (toolId) => {
        setSelectedTool(toolId);
        onOpen();
    };

    const confirmDeleteTool = async () => {
        try {
            await axios.delete(`/api/protected/tools/mcp/${selectedTool}`);
            setTools(prev => prev.filter(tool => tool._id !== selectedTool));
            onClose();
            setSelectedTool(null);

            toast({
                title: 'Success',
                description: 'MCP deleted successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
        } catch (error) {
            console.error('Error deleting mcp:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete mcp',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
    };

    const handleSave = async () => {
        // Prepare data for saving
        let dataToSave = { ...formData };

        try {
            if (editingToolId) {
                const response = await axios.put(`/api/protected/tools/mcp/${editingToolId}`, dataToSave);
                if (response.data?.success) {
                    setShowForm(false);
                    resetForm();
                    fetchTools();
                    toast({
                        title: 'Success',
                        description: 'MCP configuration updated successfully',
                        status: 'success',
                        duration: 3000,
                        isClosable: true
                    });
                } else {
                    throw new Error(response.data?.message || 'Failed to update mcp configuration');
                }
            } else {
                const response = await axios.post('/api/protected/tools/mcp', dataToSave);
                if (response.data?.success) {
                    setShowForm(false);
                    resetForm();
                    fetchTools();
                    toast({
                        title: 'Success',
                        description: 'MCP added successfully',
                        status: 'success',
                        duration: 3000,
                        isClosable: true
                    });
                } else {
                    throw new Error(response.data?.message || 'Failed to add mcp');
                }
            }
        } catch (error) {
            console.error('Error saving mcp:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || error.message || 'Failed to update mcp configuration',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
            // Don't close the form on error
            return false;
        }
        return true;
    };

    // Add this state near your other useState hooks
    const [authType, setAuthType] = useState(formData.security.tokenType || 'none');

    const [headerAuth, setHeaderAuth] = useState(
        formData.security.tokenType === 'header' ? (formData.security.headers || {}) : {}
    );

    // Update authType when editing a tool
    useEffect(() => {
        setAuthType(formData.security.tokenType || 'none');

        // // Set bearerKey if bearer, else empty
        // if (formData.security?.tokenType === 'bearer' && formData.security.headers && formData.security.headers['Authorization']) {
        //     const authHeader = formData.security.headers['Authorization'];
        //     if (typeof authHeader === 'string' && authHeader.toLowerCase().startsWith('bearer ')) {
        //         setBearerKey(authHeader.slice(7)); // Remove "Bearer "
        //     } else {
        //         setBearerKey(authHeader || '');
        //     }
        // } else {
        //     setBearerKey('');
        // }

        // Set headerAuth if header, else empty object
        if (formData.security?.tokenType === 'header') {
            setHeaderAuth(formData.security.headers || {});
        } else {
            setHeaderAuth({});
        }
    }, [formData.security]);

    // Handler for authentication type change
    const handleAuthTypeChange = (e) => {
        const value = e.target.value;
        setAuthType(value);
        setFormData((prev) => ({
            ...prev,
            security: {
                ...prev.security,
                tokenType: value,
                // Reset fields based on type
                ...(value === 'bearer' ? { bearerKey: '', headers: {} } : {}),
                ...(value === 'header' ? { headers: {}, bearerKey: '' } : {}),
            }
        }));
    };

    // Handler for bearer key change
    const handleBearerKeyChange = (e) => {
        const newBearerKey = e.target.value;
        setFormData((prev) => ({
            ...prev,
            security: {
                ...prev.security,
                tokenType: 'bearer', // ensure it's bearer if you want
                headers: {
                    ...prev.security.headers,
                    Authorization: `Bearer ${newBearerKey}`
                }
            }
        }));
    };


    // Handler for header auth change
    const handleHeaderAuthChange = (key, value) => {
        const updated = { ...headerAuth, [key]: value };
        setHeaderAuth(updated);
        setFormData((prev) => ({
            ...prev,
            security: {
                ...prev.security,
                headers: updated
            }
        }));
    };

    const handleAddHeaderField = () => {
        // Add an empty header field
        handleHeaderAuthChange('', '');
    };

    const handleRemoveHeaderField = (key) => {
        const updated = { ...headerAuth };
        delete updated[key];
        setHeaderAuth(updated);
        setFormData((prev) => ({
            ...prev,
            security: {
                ...prev.security,
                headers: updated
            }
        }));
    };

    // Add near other useState hooks
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [testTool, setTestTool] = useState(null);
    const [testParams, setTestParams] = useState({});
    const [testResult, setTestResult] = useState(null);
    const [testLoading, setTestLoading] = useState(false);

    const handleTestTool = (tool) => {
        setTestTool(tool);
        setTestParams({});
        setTestResult(null);
        setTestModalOpen(true);
    };

    const handleTestParamChange = (key, value) => {
        setTestParams((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleTestSubmit = async () => {
        setTestLoading(true);
        try {
            const response = await axios.post('/api/protected/tools/test/mcp', {
                toolId: testTool._id
            });
            setTestResult(response.data);
        } catch (error) {
            console.error('Error testing mcp:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to test mcp',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>MCP | AI Call System</title>
                <meta name="description" content="Manage your MCP and configurations" />
            </Helmet>

            <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
                <Navigation />

                <Box flex="1" p={6}>
                    <Flex justify="space-between" align="center" mb={6}>
                        <Heading size="lg" color={colorMode === 'light' ? 'gray.800' : 'white'}>MCP</Heading>
                        <Flex gap={2}>
                            <Tooltip label="Refresh tools">
                                <IconButton
                                    icon={<MdRefresh />}
                                    aria-label="Refresh tools"
                                    onClick={fetchTools}
                                    colorScheme="brand"
                                />
                            </Tooltip>

                            <Menu colorScheme="brand" >
                                <MenuButton leftIcon={<MdAdd /> } onClick={() => handleAddTool('API')} as={Button} colorScheme="brand">
                                    Add MCP
                                </MenuButton>
                            </Menu>


                        </Flex>
                    </Flex>

                    {loading ? (
                        <VStack spacing={4}>
                            {Array(3).fill(0).map((_, i) => (
                                <Skeleton key={i} height="60px" width="full" borderRadius="md" />
                            ))}
                        </VStack>
                    ) : tools.length > 0 ? (
                        <Card
                            bg={colorMode === 'light' ? 'white' : 'gray.800'}
                            borderRadius="lg"
                            boxShadow="sm"
                        >
                            <CardBody>
                                <Heading size="md" mb={4}>Configured MCP</Heading>
                                <Table variant="simple" width={"full"} size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th
                                                color={colorMode === 'light' ? 'gray.600' : 'gray.100'}
                                                fontSize="sm"
                                                fontWeight="medium"
                                                textTransform="none"
                                            >Name</Th>
                                           
                                            <Th
                                                color={colorMode === 'light' ? 'gray.600' : 'gray.100'}
                                                fontSize="sm"
                                                fontWeight="medium"
                                                textTransform="none"
                                            >URL</Th>
                                            <Th
                                                color={colorMode === 'light' ? 'gray.600' : 'gray.100'}
                                                fontSize="sm"
                                                fontWeight="medium"
                                                textTransform="none"
                                            >Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {tools.map((tool) => (
                                            <Tr key={tool._id}>
                                                <Td color={colorMode === 'light' ? 'gray.700' : 'gray.100'}>
                                                    <Flex align="center" gap={2}>
                                                        <Text fontSize="sm">{tool.name}</Text>
                                                        <Tooltip label={tool.description}>
                                                            <IconButton
                                                                icon={<MdInfo />}
                                                                aria-label={tool.description}
                                                                size="sm"
                                                                variant="ghost"
                                                            />
                                                        </Tooltip>
                                                    </Flex>
                                                </Td>
                                                
                                                <Td>
                                                    <Tooltip label={tool.url}>
                                                        <Code
                                                            fontSize="sm"
                                                            bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}
                                                            color={colorMode === 'light' ? 'gray.700' : 'gray.100'}
                                                            p={1}
                                                        >
                                                            {maskUrlForTable(tool.url)}
                                                        </Code>
                                                    </Tooltip>
                                                </Td>
                                                <Td>
                                                    <Flex gap={2}>
                                                        <Tooltip label="Edit tool">
                                                            <IconButton
                                                                icon={<MdEdit />}
                                                                aria-label="Edit tool"
                                                                colorScheme="blue"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleEditTool(tool)}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Delete tool">
                                                            <IconButton
                                                                icon={<MdDelete />}
                                                                aria-label="Delete tool"
                                                                colorScheme="red"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleDeleteTool(tool._id)}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Test tool">
                                                            <IconButton
                                                                icon={<MdPlayArrow />} // You can use another icon, e.g. MdPlayArrow
                                                                aria-label="Test tool"
                                                                colorScheme="green"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleTestTool(tool)}
                                                            />
                                                        </Tooltip>
                                                    </Flex>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    ) : (
                        <Alert status="info" borderRadius="lg">
                            <AlertIcon />
                            <Text>No MCP configured yet. Add a MCP to get started.</Text>
                        </Alert>
                    )}

                    {showForm && (
                        <Modal isOpen={showForm} onClose={handleCancel} size="6xl" closeOnOverlayClick={false}>
                            <ModalOverlay />
                            <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.900'}>
                                <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                    {editingToolId ? 'Edit MCP' : 'Add New MCP'}
                                </ModalHeader>
                                <ModalCloseButton color={colorMode === 'light' ? 'gray.800' : 'white'} />
                                <ModalBody pb={6}>
                                    <Stepper
                                        index={currentStep}
                                        mb={8}
                                        colorScheme="brand"
                                        size="lg"
                                    >
                                        {steps.map((step, index) => (
                                            <Step key={index}>
                                                <StepIndicator
                                                    bg={index <= currentStep ? 'brand.100' : (colorMode === 'light' ? 'white' : 'gray.800')}
                                                    borderColor={index <= currentStep ? 'brand.500' : (colorMode === 'light' ? 'gray.200' : 'gray.600')}
                                                >
                                                    <StepStatus
                                                        complete={<StepIcon color="brand.200" />}
                                                        incomplete={<StepNumber color={colorMode === 'light' ? 'gray.400' : 'gray.500'} />}
                                                        active={<StepNumber color="brand.500" />}
                                                    />
                                                </StepIndicator>
                                                <Box flexShrink='0'>
                                                    <StepTitle
                                                        color={index <= currentStep ? 'brand.500' : (colorMode === 'light' ? 'gray.600' : 'gray.400')}
                                                    >
                                                        {step.title}
                                                    </StepTitle>
                                                    <StepDescription color={colorMode === 'light' ? 'gray.600' : 'gray.400'}>
                                                        {step.description}
                                                    </StepDescription>
                                                </Box>
                                                <StepSeparator
                                                    borderColor={index < currentStep ? 'brand.500' : (colorMode === 'light' ? 'gray.200' : 'gray.600')}
                                                    _horizontal={{
                                                        borderWidth: '2px'
                                                    }}
                                                />
                                            </Step>
                                        ))}
                                    </Stepper>

                                    {currentStep === 0 && (
                                        <VStack spacing={4} align="stretch">
                                            <FormControl isRequired isInvalid={formErrors.name}>
                                                <FormLabel color={currentStep >= 0 ? (colorMode === 'light' ? 'brand.900' : 'brand.300') : (colorMode === 'light' ? 'gray.600' : 'gray.400')}>MCP Name</FormLabel>
                                                <Input
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleToolNameChange}
                                                    placeholder="Enter MCP name"
                                                    bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                    borderColor={colorMode === 'light' ? 'brand.900' : 'brand.400'}
                                                    _hover={{
                                                        borderColor: 'brand.500'
                                                    }}
                                                    _focus={{
                                                        borderColor: 'brand.500',
                                                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                                                    }}
                                                    _placeholder={{
                                                        color: colorMode === 'light' ? 'gray.400' : 'gray.500'
                                                    }}
                                                />
                                                {formErrors.name && (
                                                    <Text color="red.400" fontSize="sm" mt={1}>
                                                        {formErrors.name}
                                                    </Text>
                                                )}
                                            </FormControl>

                                           
                                            <HStack>
                                               
                                                <Box flex="0 0 80%">

                                                    <FormControl isRequired isInvalid={formErrors.url}>
                                                        <FormLabel color={currentStep >= 0 ? (colorMode === 'light' ? 'brand.900' : 'brand.300') : (colorMode === 'light' ? 'gray.600' : 'gray.400')}>URL</FormLabel>
                                                        <Input
                                                            name="url"
                                                            value={formData.url}
                                                            onChange={handleUrlChange}
                                                            placeholder="Enter tool URL"
                                                            bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                                            color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                            borderColor={colorMode === 'light' ? 'brand.900' : 'brand.400'}
                                                            _hover={{
                                                                borderColor: 'brand.500'
                                                            }}
                                                            _focus={{
                                                                borderColor: 'brand.500',
                                                                boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
                                                            }}
                                                            _placeholder={{
                                                                color: colorMode === 'light' ? 'gray.400' : 'gray.500'
                                                            }}
                                                        />
                                                        {formErrors.url && (
                                                            <Text color="red.400" fontSize="sm" mt={1}>
                                                                {formErrors.url}
                                                            </Text>
                                                        )}
                                                    </FormControl>
                                                </Box>
                                            </HStack>
                                          
                                        </VStack>
                                    )}

                                    {currentStep === 1 && (
                                        <VStack spacing={4}>
                                            <FormControl>
                                                <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>
                                                    Authentication Type
                                                </FormLabel>
                                                <Select
                                                    value={authType}
                                                    onChange={handleAuthTypeChange}
                                                    bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                    borderColor={colorMode === 'light' ? 'brand.500' : 'brand.400'}
                                                >
                                                    <option value="none">None</option>
                                                    <option value="bearer">Bearer Token</option>
                                                    <option value="header">Header Auth</option>
                                                </Select>
                                            </FormControl>

                                            {authType === 'bearer' && (
                                                <FormControl>
                                                    <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>
                                                        Bearer Token
                                                    </FormLabel>
                                                    <Input
                                                        value={formData.security?.headers?.Authorization?.replace(/^Bearer /i, '') || ''}
                                                        onChange={handleBearerKeyChange}
                                                        placeholder="Enter Bearer Token"
                                                        bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                                        color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                        borderColor={colorMode === 'light' ? 'brand.500' : 'brand.400'}
                                                    />
                                                </FormControl>
                                            )}

                                            {authType === 'header' && (
                                                <FormControl>
                                                    <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>
                                                        Custom Headers
                                                    </FormLabel>
                                                    <VStack align="stretch" spacing={2}>
                                                        {Object.entries(headerAuth).map(([key, value], idx) => (
                                                            <HStack key={idx}>
                                                                <Input
                                                                    value={key}
                                                                    onChange={e => {
                                                                        const newKey = e.target.value;
                                                                        // Remove old key, add new key with same value
                                                                        const updated = { ...headerAuth };
                                                                        delete updated[key];
                                                                        updated[newKey] = value;
                                                                        setHeaderAuth(updated);
                                                                        setFormData((prev) => ({
                                                                            ...prev,
                                                                            security: {
                                                                                ...prev.security,
                                                                                headers: updated
                                                                            }
                                                                        }));
                                                                    }}
                                                                    placeholder="Header Key"
                                                                    bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                                    borderColor={colorMode === 'light' ? 'brand.500' : 'brand.400'}
                                                                />
                                                                <Input
                                                                    value={value}
                                                                    onChange={e => handleHeaderAuthChange(key, e.target.value)}
                                                                    placeholder="Header Value"
                                                                    bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                                                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                                    borderColor={colorMode === 'light' ? 'brand.500' : 'brand.400'}
                                                                />
                                                                <Button
                                                                    colorScheme="red"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveHeaderField(key)}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </HStack>
                                                        ))}
                                                        <Button onClick={handleAddHeaderField} colorScheme="brand" size="sm">
                                                            Add Header
                                                        </Button>
                                                    </VStack>
                                                </FormControl>
                                            )}

                                        </VStack>
                                    )}

                                    {(currentStep === 2 ) && (
                                        <VStack spacing={4} align="stretch">
                                            <Text fontWeight="bold" color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>Review MCP Configuration</Text>
                                            <Divider borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} />

                                            <Box bg={colorMode === 'light' ? 'white' : 'gray.800'} p={4} borderRadius="md" borderWidth="1px" borderColor={colorMode === 'light' ? 'brand.500' : 'brand.400'}>
                                                <VStack align="stretch" spacing={3}>
                                                    <FormControl>
                                                        <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>Name</FormLabel>
                                                        <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>{formData.name}</Text>
                                                    </FormControl>

                                                    <FormControl>
                                                        <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>Description</FormLabel>
                                                        <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>{formData.description}</Text>
                                                    </FormControl>

                                                    <FormControl>
                                                        <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>URL</FormLabel>
                                                        <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>{formData.url}</Text>
                                                    </FormControl>

                                                </VStack>
                                            </Box>

                                            <Text fontWeight="bold" color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>Security Settings</Text>
                                            <Box bg={colorMode === 'light' ? 'white' : 'gray.800'} p={4} borderRadius="md" borderWidth="1px" borderColor={colorMode === 'light' ? 'brand.500' : 'brand.400'}>
                                                <VStack align="stretch" spacing={3}>
                                                    <FormControl>
                                                        <FormLabel color={colorMode === 'light' ? 'brand.500' : 'brand.300'}>Token Type</FormLabel>
                                                        <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>{formData.security.tokenType || 'None'}</Text>
                                                    </FormControl>
                                                </VStack>
                                            </Box>
                                        </VStack>
                                    )}
                                </ModalBody>
                                <ModalFooter
                                    borderTop="1px solid"
                                    borderColor={colorMode === 'light' ? 'gray.100' : 'gray.700'}
                                    mt={4}
                                >
                                    <Button
                                        variant="ghost"
                                        mr={3}
                                        onClick={handleCancel}
                                        color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                                        _hover={{
                                            bg: colorMode === 'light' ? 'gray.100' : 'gray.700'
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {currentStep > 0 && (
                                        <Button
                                            variant="ghost"
                                            mr={3}
                                            onClick={handlePrev}
                                            color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                                            _hover={{
                                                bg: colorMode === 'light' ? 'gray.100' : 'gray.700'
                                            }}
                                        >
                                            Previous
                                        </Button>
                                    )}
                                    {currentStep < steps.length - 1 ? (
                                        <Button
                                            colorScheme="brand"
                                            onClick={handleNext}
                                            _hover={{
                                                bg: 'brand.500'
                                            }}
                                        >
                                            Next
                                        </Button>
                                    ) : (
                                        <Button
                                            colorScheme="brand"
                                            onClick={handleSave}
                                            _hover={{
                                                bg: 'brand.500'
                                            }}
                                        >
                                            Save
                                        </Button>
                                    )}
                                </ModalFooter>
                            </ModalContent>
                        </Modal>
                    )}

                    {/* Delete Confirmation Modal */}
                    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                        <ModalOverlay />
                        <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
                            <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>Delete Tool</ModalHeader>
                            <ModalCloseButton color={colorMode === 'light' ? 'gray.600' : 'gray.400'} />
                            <ModalBody>
                                <Text color={colorMode === 'light' ? 'gray.700' : 'gray.100'}>
                                    Are you sure you want to delete this MCP? This action cannot be undone.
                                </Text>
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant="ghost"
                                    mr={3}
                                    onClick={onClose}
                                    color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                                    _hover={{
                                        bg: colorMode === 'light' ? 'gray.100' : 'gray.700'
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme="red"
                                    onClick={confirmDeleteTool}
                                    variant="solid"
                                >
                                    Delete
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>

                    {/* Test Tool Modal */}
                    <Modal isOpen={testModalOpen} onClose={() => setTestModalOpen(false)} size="lg" >
                        <ModalOverlay />
                        <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'} pb={10}>
                            <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>
                                Test Tool - {testTool?.name}
                            </ModalHeader>
                            <ModalCloseButton color={colorMode === 'light' ? 'gray.600' : 'gray.400'} />
                            <ModalBody>
                                <VStack spacing={4} align="stretch">
                                    <Text color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>
                                        Fill up the parameters below to test the MCP.
                                    </Text>

                                    <Button
                                        colorScheme="brand"
                                        onClick={handleTestSubmit}
                                        isLoading={testLoading}
                                        loadingText="Testing..."
                                    >
                                        Test MCP
                                    </Button>

                                    {testResult && (
                                        <Box
                                            p={4}
                                            borderRadius="md"
                                            bg={testResult.success? colorMode === 'light' ? 'green.400' : 'green.900' : colorMode === 'light' ? 'red.50' : 'red.600'}
                                            color={colorMode === 'light' ? 'green.800' : 'green.100'}
                                            borderWidth="1px"
                                            borderColor={colorMode === 'light' ? 'green.200' : 'green.700'}
                                        >
                                            <Text colorScheme="brand" fontWeight="semibold" mb={2}>Test Result:</Text>
                                            <Code
                                                p={2}
                                                borderRadius="md"
                                                bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}
                                                color={colorMode === 'light' ? 'gray.800' : 'white'}
                                                display="block"
                                                whiteSpace="pre-wrap"
                                            >
                                                {JSON.stringify(testResult, null, 2)}
                                            </Code>
                                        </Box>
                                    )}
                                </VStack>
                            </ModalBody>
                        </ModalContent>
                    </Modal>
                </Box>
            </Flex>
        </>
    );
};

export default MCPPage;
